import React from "react";
import { ProblemDetails } from "data/types";
import Button from "react-bootstrap/lib/Button";
import ButtonGroup from "react-bootstrap/lib/ButtonGroup";
import { get } from "data/constants";
import { getLocalizedText } from "data/utils";
import { toastActionResult, toastStyled, toastTypes } from "data/toast";
import copy from "copy-to-clipboard";
import { JL } from "data/logging";
import styles from "./apiError.module.css";

export const HTTP_ERROR_UNAUTHORIZED = 401;
export const HTTP_ERROR_FORBIDDEN = 403;
export const HTTP_ERROR_REQUEST_TIMEOUT = 408;
export const HTTP_ERROR_CONFLICT = 412;
export const HTTP_ERROR_INTERNAL_SERVER_ERROR = 500;

const _log = JL("apiError");
export class ApiErrorContainer extends Error {
    data: ProblemDetails;

    constructor(response: ProblemDetails) {
        super(response.title);
        /* istanbul ignore else */
        if (Error.captureStackTrace)
            Error.captureStackTrace(this, ApiErrorContainer);
        this.data = response;
    }
}

export class ApiError extends ApiErrorContainer {
    constructor(...args) {
        super({
            title: args.length > 0 ? args[0] : "Unknown",
            status: 500,
        });
    }
}

export class ApiNetworkError extends ApiErrorContainer {
    constructor(...args) {
        const response: Response = args[0];
        super({
            status: response.status,
            title: response.statusText,
            ...(args.length > 1 ? args[1] : {}),
        });
    }
}

export class ApiConflictError extends ApiNetworkError {}

export class ApiForbiddenError extends ApiNetworkError {}

export class ApiNetworkOfflineError extends ApiErrorContainer {
    constructor(...args) {
        const detail = args.length > 1 ? args[1] : {};
        /* istanbul ignore else */
        if (detail.extensions == null) {
            detail.extensions = {};
        }
        const extensions = {
            eventId: 0,
            detail,
        };
        super({
            title: args[0],
            extensions,
        });
    }
}

export const notifyOnForbidden = (response) => {
    _log.error(() => ({
        msg: "Fetch.Api.Forbidden",
        response,
    }));
    const { email = false, url = false } = get(
        window.CONFIG,
        ["general", "localContactInfo"],
        {}
    );
    const details =
        email || /* istanbul ignore next */ url
            ? () => (
                  <ButtonGroup bsSize="small" className={styles.group}>
                      {email && (
                          <Button
                              bsStyle="link"
                              className={styles.btn}
                              href={`mailto:${email}`}
                          >
                              {email}
                          </Button>
                      )}
                      {url && (
                          <Button
                              className={styles.btn}
                              bsStyle="link"
                              href={url}
                              target="_blank"
                              rel="noopener noreferrer"
                          >
                              {url}
                          </Button>
                      )}
                  </ButtonGroup>
              )
            : null;

    toastStyled({
        toastId: "fetch-forbidden",
        ...toastTypes.partial,
        autoClose: false,
        details,
        message: getLocalizedText("$fetch.forbidden"),
    });
};

export const notifyOnError = (response) => {
    _log.error(() => ({
        msg: "Fetch.Api.Error",
        response,
    }));

    if (!response.extra || response.extra.notify !== true) {
        return;
    }

    const { extra, isError, ...rest } = response;
    /* istanbul ignore next */
    const {
        email = false,
        text = "",
        url = false,
    } = get(window.CONFIG, ["general", "localContactInfo"], {});
    const message = getLocalizedText("$fetch.clipboard_copy.message");
    const handleClick = () =>
        copy(JSON.stringify(rest), {
            message,
            format: "text/plain",
            onCopy: /* istanbul ignore next */ (clipboardData: Object) =>
                toastActionResult(true, "fetch.clipboard_copy"),
        });

    const details = () => (
        <ButtonGroup bsSize="small" className={styles.group}>
            {email && (
                <Button
                    className={styles.btn}
                    bsStyle="link"
                    href={`mailto:${email}`}
                >
                    {email}
                </Button>
            )}
            {url && (
                <Button
                    className={styles.btn}
                    bsStyle="link"
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                >
                    {url}
                </Button>
            )}
            <Button className={styles.btn} bsStyle="link" onClick={handleClick}>
                {message}
            </Button>
        </ButtonGroup>
    );

    toastStyled({
        ...toastTypes.failure,
        autoClose: false,
        details,
        message: getLocalizedText(response.extra.loc || "$fetch.error", {
            text,
        }),
    });
};
